/**
 * EnemyMovement — extracted ground/flyer movement helpers.
 * Each function takes the Enemy instance as the first parameter.
 */
import { TILE_SIZE, GAME_WIDTH, GAME_HEIGHT, FLYING_ENEMY_TYPES, TileType, MUD_SPEED_MULTIPLIER, AGGRO_RANGE, DecorInteraction, DECOR_INTERACTIONS, EnemyType } from '@/config/GameConfig';
import { MAP_GRID, getDecorAt } from '@/map/MapData';
import { GOD } from '@/config/GodMode';
import { findPathToBase, findPathToPoint } from '@/systems/Pathfinder';
import { hasLineOfSight } from '@/systems/LineOfSight';
import { Enemy } from '@/entities/Enemy';

/**
 * Close-in factor: 0 when enemy is firing normally, ramps to 1 when stuck orbiting.
 * Used to shrink idealDist and boost radial approach force.
 */
function getCloseInFactor(enemy: Enemy): number {
  if (enemy.notFiringTimer <= GOD.closeInDelay) return 0;
  const elapsed = enemy.notFiringTimer - GOD.closeInDelay;
  return Math.min(1, elapsed / GOD.closeInRampTime);
}

/**
 * Hard repulsion for ranged enemies that are too close to their target.
 * Returns a strong outward steering vector when within rangedKeepDistPx.
 * This prevents ranged enemies from overlapping with robots.
 */
function getRangedRepulsion(enemy: Enemy, targetX: number, targetY: number): { rx: number; ry: number } {
  const dx = enemy.body.x - targetX;
  const dy = enemy.body.y - targetY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const keepDist = enemy.collisionRadius + GOD.rangedKeepDistPx;
  if (dist >= keepDist || dist < 0.01) return { rx: 0, ry: 0 };
  // Strength ramps from 0 at keepDist to 4.0 at overlap (overwhelming other forces)
  const t = 1 - dist / keepDist;
  const strength = t * 4.0;
  const nx = dx / dist;
  const ny = dy / dist;
  return { rx: nx * strength, ry: ny * strength };
}

// ────────────────────────────────────────────────────────
// Ground movement
// ────────────────────────────────────────────────────────

/** Standard ground enemy: follow waypoints (or rush a robot if aggro). */
export function updateGround(enemy: Enemy, dt: number): void {
  // firingTarget is precomputed by precomputeFiringTarget() before this frame

  // Already sieging the base — do melee damage if melee-only
  if (enemy.attackingBase) {
    updateBaseSiegeMovement(enemy, dt);
    return;
  }

  // A* recalculation when path is dirty (pushed off-path by collision)
  if (enemy.pathRecalcCooldown > 0) enemy.pathRecalcCooldown -= dt;
  if (enemy.pathDirty && enemy.pathRecalcCooldown <= 0) {
    recalcPath(enemy);
  }

  // Check proximity to final waypoint (base) — stop at weapon range or melee range
  const baseWp = enemy.waypoints[enemy.waypoints.length - 1];
  if (baseWp) {
    const bx = baseWp.x - enemy.body.x;
    const by = baseWp.y - enemy.body.y;
    const baseDist = Math.sqrt(bx * bx + by * by);
    // Ranged enemies stop at their longest weapon range; melee-only stop at ~0.7 tiles
    const stopRange = enemy.weaponDefs.length > 0
      ? Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult))
      : TILE_SIZE * 0.7;
    if (baseDist <= stopRange) {
      // firingTarget already set by precomputeFiringTarget() for base proximity
      enemy.onReachedBase();
      // Arc formation for base siege (ranged enemies)
      if (enemy.weaponDefs.length > 0) {
        const step = enemy.speed * dt * 0.4;
        let sx = enemy.separationX + enemy.arcSteerX;
        let sy = enemy.separationY + enemy.arcSteerY;
        const cif = getCloseInFactor(enemy);
        const idealDist = stopRange * 0.82 * (1 - cif * (1 - GOD.closeInMaxShrink));
        const distErr = (baseDist - idealDist) / idealDist;
        const radialWeight = 0.5 + cif * 1.5; // 0.5 → 2.0
        sx += (bx / baseDist) * distErr * radialWeight;
        sy += (by / baseDist) * distErr * radialWeight;
        const sl = Math.sqrt(sx * sx + sy * sy);
        if (sl > 0.05) {
          const clampedMag = Math.min(sl, 1) * step;
          enemy.body.setPosition(
            enemy.body.x + (sx / sl) * clampedMag,
            enemy.body.y + (sy / sl) * clampedMag,
          );
        }
      }
      return;
    }
  }

  // Blocked by a robot with stabilization legs: stop and attack it
  // Runners ignore blockers — they rush past everything toward the base
  if (enemy.blockingRobot && enemy.blockingRobot.alive && enemy.enemyType !== EnemyType.RUNNER) {
    const dx = enemy.blockingRobot.x - enemy.body.x;
    const dy = enemy.blockingRobot.y - enemy.body.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < TILE_SIZE * 1.5) {
      // Move toward blocker and deal contact damage
      const mudMult = getMudMultiplier(enemy);
      const step = enemy.speed * dt * mudMult;
      if (dist > TILE_SIZE * 0.5) {
        enemy.body.setPosition(
          enemy.body.x + (dx / dist) * step,
          enemy.body.y + (dy / dist) * step,
        );
      }
      // Deal melee damage to the blocking robot (synced with lunge cooldown + global weapon lock)
      if (enemy.meleeCooldown <= 0 && enemy.weaponLockTimer <= 0) {
        enemy.blockingRobot.takeDamage(enemy.baseDamage, enemy.body.x, enemy.body.y);
        enemy.meleeCooldown = enemy.meleeCooldownDuration;
        enemy.weaponLockTimer = Math.max(Enemy.WEAPON_LOCK_MIN, enemy.meleeCooldownDuration * 0.5);
        enemy.tryMeleeLunge(enemy.blockingRobot.x, enemy.blockingRobot.y);
      }
      return;
    }
  }

  // Terrain hazard check: ground enemies start dying animation on WATER, MAGMA, or KILL tiles
  {
    const col = Math.floor(enemy.body.x / TILE_SIZE);
    const row = Math.floor(enemy.body.y / TILE_SIZE);
    if (row >= 0 && row < MAP_GRID.length && col >= 0 && col < (MAP_GRID[0]?.length ?? 0)) {
      const tile = MAP_GRID[row][col];
      if (tile === TileType.WATER) { enemy.startTerrainDeath('water'); return; }
      if (tile === TileType.MAGMA) { enemy.startTerrainDeath('magma'); return; }
      const decor = getDecorAt(col, row);
      if (decor && DECOR_INTERACTIONS[decor.type].interaction === DecorInteraction.KILL) {
        enemy.startTerrainDeath('chasm'); return;
      }
    }
  }

  // Mud / decoration slowdown: check tile under this enemy
  const mudMult = getMudMultiplier(enemy);
  // Dash speed multiplier (Runner)
  const dashMult = enemy.isDashing ? Enemy.DASH_SPEED_MULT : 1;

  // Camo confusion: enemy sensed a cloaked robot — scatter erratically
  if (enemy.camoConfusionTimer > 0) {
    enemy.camoConfusionTimer -= dt;
    const step = enemy.speed * dt * mudMult * 0.5; // half speed during panic
    // Erratic jitter: random direction changes simulating panic scanning
    const jitterAngle = Math.sin(enemy.camoConfusionTimer * 12) * Math.PI * 0.6;
    const baseAngle = Math.atan2(
      enemy.waypoints[enemy.currentWaypointIndex]?.y ?? enemy.body.y - enemy.body.x,
      enemy.waypoints[enemy.currentWaypointIndex]?.x ?? enemy.body.x + 1 - enemy.body.x,
    );
    const angle = baseAngle + jitterAngle;
    enemy.body.setPosition(
      enemy.body.x + Math.cos(angle) * step * 0.3,
      enemy.body.y + Math.sin(angle) * step * 0.3,
    );
    // Visual feedback: rapid flip to simulate looking around
    if (Math.sin(enemy.camoConfusionTimer * 8) > 0) {
      enemy.body.setFlipX(!enemy.body.flipX);
    }
    return;
  }

  // Aggro: rush toward a nearby player robot instead of following path
  // Ranged enemies with weapons stop at firing range instead of charging into melee
  if (enemy.aggroTarget && !enemy.inMeleeContact) {
    const dx = enemy.aggroTarget.x - enemy.body.x;
    const dy = enemy.aggroTarget.y - enemy.body.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Ranged aggro: if precomputeFiringTarget() set a target, do arc formation
    if (enemy.weaponDefs.length > 0 && enemy.firingTarget) {
      const maxWeaponRange = Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult));
      // Arc formation: lateral movement to spread out (magnitude-based, no normalization)
      const cif = getCloseInFactor(enemy);
      const step = enemy.speed * dt * mudMult * (0.6 + cif * 0.4); // speed up approach when closing in
      let sx = enemy.arcSteerX * (2.0 * (1 - cif * 0.7)) + enemy.separationX * (1.5 * (1 - cif * 0.7));
      let sy = enemy.arcSteerY * (2.0 * (1 - cif * 0.7)) + enemy.separationY * (1.5 * (1 - cif * 0.7));
      // Correct distance to ideal firing range — shrinks when not firing
      const idealDist = maxWeaponRange * 0.80 * (1 - cif * (1 - GOD.closeInMaxShrink));
      const distErr = (dist - idealDist) / idealDist;
      const radialWeight = 0.8 + cif * 2.0; // 0.8 → 2.8 (overwhelms arc/sep forces)
      sx += (dx / dist) * distErr * radialWeight;
      sy += (dy / dist) * distErr * radialWeight;
      // Hard repulsion when too close — prevents sprite overlap
      const rep = getRangedRepulsion(enemy, enemy.aggroTarget!.x, enemy.aggroTarget!.y);
      sx += rep.rx;
      sy += rep.ry;
      // Clamp magnitude instead of normalizing — lets movement decay when close to target
      const sl = Math.sqrt(sx * sx + sy * sy);
      if (sl > 0.05) {
        const clampedMag = Math.min(sl, 1) * step;
        enemy.body.setPosition(
          enemy.body.x + (sx / sl) * clampedMag,
          enemy.body.y + (sy / sl) * clampedMag,
        );
      }
      return;
    }

    const step = enemy.speed * dt * mudMult * dashMult;
    if (dist > 4) {
      // A* detour: when stuck behind a wall, pathfind around it
      if (enemy.aggroStuckTimer > 0.3) {
        // If we now have line-of-sight, drop detour and resume direct pursuit
        if (enemy.aggroDetourPath && !enemy.isFlyer &&
          hasLineOfSight(enemy.body.x, enemy.body.y, enemy.aggroTarget!.x, enemy.aggroTarget!.y)) {
          enemy.aggroDetourPath = null;
          enemy.aggroStuckTimer = 0;
          enemy.aggroLastDist = dist;
          // Fall through to direct pursuit below
        } else {
          // Compute detour path once when threshold crossed
          if (!enemy.aggroDetourPath) {
            const detour = findPathToPoint(enemy.body.x, enemy.body.y, enemy.aggroTarget!.x, enemy.aggroTarget!.y);
            if (detour && detour.length > 1) {
              enemy.aggroDetourPath = detour;
              enemy.aggroDetourIndex = 1; // skip first (current tile)
            }
          }
          // Follow the detour path
          if (enemy.aggroDetourPath && enemy.aggroDetourIndex < enemy.aggroDetourPath.length) {
            const wp = enemy.aggroDetourPath[enemy.aggroDetourIndex];
            const wdx = wp.x - enemy.body.x;
            const wdy = wp.y - enemy.body.y;
            const wdist = Math.sqrt(wdx * wdx + wdy * wdy);
            if (wdist <= step) {
              enemy.body.setPosition(wp.x, wp.y);
              enemy.aggroDetourIndex++;
            } else {
              enemy.body.setPosition(
                enemy.body.x + (wdx / wdist) * step,
                enemy.body.y + (wdy / wdist) * step,
              );
            }
            // Reset stuck timer as we're making progress on detour
            enemy.aggroStuckTimer = 0.31; // keep above threshold to stay in detour mode
            enemy.aggroLastDist = dist;
            return;
          }
          // Detour exhausted or failed — clear it, let stuck timer continue
          enemy.aggroDetourPath = null;
        }
      } else {
        // Not stuck — clear any stale detour path
        enemy.aggroDetourPath = null;
      }

      // Approaching target: blend pursuit + separation + early arc spread
      let mx = dx / dist;
      let my = dy / dist;

      // Transition zone (1.0x–1.4x weapon range): spread laterally
      // Arc steering only — no separation push to avoid back-and-forth
      if (enemy.weaponDefs.length > 0 && enemy.aggroStuckTimer <= 0.5) {
        const maxWR = Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult));
        const spreadZone = maxWR * 1.4;
        if (dist < spreadZone) {
          // 0 at spreadZone edge → 1 at weapon range
          const t = Math.max(0, 1 - (dist - maxWR) / (spreadZone - maxWR));
          mx += enemy.arcSteerX * t * 2.0;
          my += enemy.arcSteerY * t * 2.0;
        } else {
          mx += enemy.separationX * 0.3;
          my += enemy.separationY * 0.3;
        }
      } else if (enemy.aggroStuckTimer <= 0.5) {
        mx += enemy.separationX * 0.3;
        my += enemy.separationY * 0.3;
      }

      // Hard repulsion for ranged enemies getting too close during approach
      if (enemy.weaponDefs.length > 0 && enemy.aggroTarget) {
        const rep = getRangedRepulsion(enemy, enemy.aggroTarget.x, enemy.aggroTarget.y);
        mx += rep.rx;
        my += rep.ry;
      }

      const ml = Math.sqrt(mx * mx + my * my);
      if (ml > 0.001) { mx /= ml; my /= ml; }
      enemy.body.setPosition(
        enemy.body.x + mx * step,
        enemy.body.y + my * step,
      );
    }
    return; // skip normal waypoint movement while aggroing
  }
  // If aggroing but in melee contact, stay put (melee DPS still applies via RobotManager)
  // Exception: melee-only enemies (no weapons) must keep pressing toward their target
  if (enemy.aggroTarget && enemy.inMeleeContact && enemy.weaponDefs.length > 0) {
    return;
  }

  // Weapon engagement: ranged enemies stop on their path if precomputed firing target exists
  // Runners never stop — they rush straight to the base
  if (enemy.weaponDefs.length > 0 && enemy.firingTarget && !enemy.aggroTarget && enemy.enemyType !== EnemyType.RUNNER) {
    const maxRange = Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult));
    const rdx = enemy.firingTarget.x - enemy.body.x;
    const rdy = enemy.firingTarget.y - enemy.body.y;
    const closestDist = Math.sqrt(rdx * rdx + rdy * rdy);
    const cif = getCloseInFactor(enemy);
    const idealDist = maxRange * 0.80 * (1 - cif * (1 - GOD.closeInMaxShrink));
    const distErr = (closestDist - idealDist) / idealDist;
    const radialWeight = 0.8 + cif * 2.0;
    const step = enemy.speed * dt * mudMult * (0.6 + cif * 0.4);
    let sx = enemy.arcSteerX * (2.0 * (1 - cif * 0.7)) + enemy.separationX * (1.5 * (1 - cif * 0.7));
    let sy = enemy.arcSteerY * (2.0 * (1 - cif * 0.7)) + enemy.separationY * (1.5 * (1 - cif * 0.7));
    sx += (rdx / closestDist) * distErr * radialWeight;
    sy += (rdy / closestDist) * distErr * radialWeight;
    // Hard repulsion when too close — prevents sprite overlap
    const rep = getRangedRepulsion(enemy, enemy.firingTarget.x, enemy.firingTarget.y);
    sx += rep.rx;
    sy += rep.ry;
    // Exponential smoothing to prevent lateral flickering (lerp toward new direction)
    const smoothing = GOD.enemySteerSmoothing ?? 0.15;
    enemy._smoothSteerX = enemy._smoothSteerX * (1 - smoothing) + sx * smoothing;
    enemy._smoothSteerY = enemy._smoothSteerY * (1 - smoothing) + sy * smoothing;
    sx = enemy._smoothSteerX;
    sy = enemy._smoothSteerY;
    // Clamp magnitude instead of normalizing — lets movement decay when close to target
    const sl = Math.sqrt(sx * sx + sy * sy);
    if (sl > 0.05) {
      const clampedMag = Math.min(sl, 1) * step;
      enemy.body.setPosition(enemy.body.x + (sx / sl) * clampedMag, enemy.body.y + (sy / sl) * clampedMag);
    }
    return;
  }

  const target = enemy.waypoints[enemy.currentWaypointIndex];
  if (!target) {
    enemy.onReachedBase();
    return;
  }

  const dx = target.x - enemy.body.x;
  const dy = target.y - enemy.body.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const step = enemy.speed * dt * mudMult * dashMult;

  if (dist <= step) {
    enemy.body.setPosition(target.x, target.y);
    enemy.currentWaypointIndex++;
    if (enemy.currentWaypointIndex >= enemy.waypoints.length) {
      enemy.onReachedBase();
    }
  } else {
    const nx = dx / dist;
    const ny = dy / dist;
    enemy.body.setPosition(enemy.body.x + nx * step, enemy.body.y + ny * step);
  }
  // Flip sprite to face movement direction (with cooldown to prevent flicker)
  if (Math.abs(dx) > GOD.flipDeadzone) {
    const wantFlip = dx < 0;
    if (wantFlip !== enemy.body.flipX) {
      const now = enemy.scene.time.now;
      if (now - enemy.lastFlipTime >= GOD.flipCooldownMs) {
        enemy.body.setFlipX(wantFlip);
        enemy.lastFlipTime = now;
      }
    }
  }
}

// ────────────────────────────────────────────────────────
// Flyer movement
// ────────────────────────────────────────────────────────

/** Flyer: follow generated flight waypoints (or rush a robot if aggro). */
export function updateFlyer(enemy: Enemy, dt: number): void {
  // Already sieging the base
  if (enemy.attackingBase) {
    updateBaseSiegeMovement(enemy, dt);
    return;
  }

  // Camo confusion: flyer circles erratically
  if (enemy.camoConfusionTimer > 0) {
    enemy.camoConfusionTimer -= dt;
    const step = enemy.speed * dt * 0.6;
    // Tight circling pattern during confusion
    const circleAngle = enemy.camoConfusionTimer * 5;
    enemy.body.setPosition(
      enemy.body.x + Math.cos(circleAngle) * step,
      enemy.body.y + Math.sin(circleAngle) * step,
    );
    enemy.body.setFlipX(Math.cos(circleAngle) < 0);
    return;
  }

  // Aggro: rush toward a nearby player robot instead of following flight path
  if (enemy.aggroTarget) {
    const dx = enemy.aggroTarget.x - enemy.body.x;
    const dy = enemy.aggroTarget.y - enemy.body.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const step = enemy.speed * dt;
    if (dist > 4) {
      // Blend pursuit with separation steering
      const sepWeight = 0.45;
      let mx = dx / dist + enemy.separationX * sepWeight;
      let my = dy / dist + enemy.separationY * sepWeight;
      // Ranged flyers: hard repulsion when too close
      if (enemy.weaponDefs.length > 0) {
        const rep = getRangedRepulsion(enemy, enemy.aggroTarget.x, enemy.aggroTarget.y);
        mx += rep.rx;
        my += rep.ry;
      }
      const ml = Math.sqrt(mx * mx + my * my);
      if (ml > 0.001) { mx /= ml; my /= ml; }
      enemy.body.setPosition(
        enemy.body.x + mx * step,
        enemy.body.y + my * step,
      );
    }
    if (Math.abs(dx) > 0.1) enemy.body.setFlipX(dx < 0);
    return;
  }

  // Check distance to base for flyers too
  const baseWp = enemy.waypoints[enemy.waypoints.length - 1];
  if (baseWp) {
    const bx = baseWp.x - enemy.body.x;
    const by = baseWp.y - enemy.body.y;
    const baseDist = Math.sqrt(bx * bx + by * by);
    const stopRange = enemy.weaponDefs.length > 0
      ? Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult))
      : TILE_SIZE * 0.7;
    if (baseDist <= stopRange) {
      enemy.onReachedBase();
      return;
    }
  }

  if (enemy.flyerWpIndex >= enemy.flyerPath.length) {
    enemy.onReachedBase();
    return;
  }

  const target = enemy.flyerPath[enemy.flyerWpIndex];
  const dx = target.x - enemy.body.x;
  const dy = target.y - enemy.body.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const step = enemy.speed * dt;

  if (dist <= step) {
    enemy.body.setPosition(target.x, target.y);
    enemy.flyerWpIndex++;
    if (enemy.flyerWpIndex >= enemy.flyerPath.length) {
      enemy.onReachedBase();
    }
  } else {
    enemy.body.setPosition(
      enemy.body.x + (dx / dist) * step,
      enemy.body.y + (dy / dist) * step,
    );
  }
  // Flip sprite to face movement direction (with cooldown to prevent flicker)
  if (Math.abs(dx) > GOD.flipDeadzone) {
    const wantFlip = dx < 0;
    if (wantFlip !== enemy.body.flipX) {
      const now = enemy.scene.time.now;
      if (now - enemy.lastFlipTime >= GOD.flipCooldownMs) {
        enemy.body.setFlipX(wantFlip);
        enemy.lastFlipTime = now;
      }
    }
  }
}

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

/** Returns MUD_SPEED_MULTIPLIER if this ground enemy is on a MUD tile, else 1.
 *  Hive enemies move 1.5× faster on mud (mud is their natural habitat). */
export function getMudMultiplier(enemy: Enemy): number {
  const col = Math.floor(enemy.body.x / TILE_SIZE);
  const row = Math.floor(enemy.body.y / TILE_SIZE);
  let mult = 1;
  if (row >= 0 && row < MAP_GRID.length && col >= 0 && col < (MAP_GRID[0]?.length ?? 0)) {
    if (MAP_GRID[row][col] === TileType.MUD) {
      mult = MUD_SPEED_MULTIPLIER;
      // Hive thrives on mud — 1.5× speed bonus (effectively 0.4 × 1.5 = 0.6)
      if (enemy.enemyType === EnemyType.HIVE) mult *= 1.5;
    }
    // Decoration slow (e.g. puddle)
    const decor = getDecorAt(col, row);
    if (decor) {
      const cfg = DECOR_INTERACTIONS[decor.type];
      if (cfg.interaction === DecorInteraction.SLOW && cfg.slowMultiplier) {
        mult = Math.min(mult, cfg.slowMultiplier);
      }
    }
  }
  return mult;
}

/**
 * Generate a realistic flight path from spawn to base.
 * Places 3–5 random intermediate waypoints anywhere on the map,
 * biased toward progressing from spawn to base.
 */
export function generateFlightPath(
  start: { x: number; y: number },
  end: { x: number; y: number },
): { x: number; y: number }[] {
  const path: { x: number; y: number }[] = [{ x: start.x, y: start.y }];
  const margin = TILE_SIZE;

  // Number of intermediate waypoints (3–5)
  const numWp = 3 + Math.floor(Math.random() * 3);

  for (let i = 1; i <= numWp; i++) {
    // Progress fraction from spawn to base (with some randomness)
    const baseFrac = i / (numWp + 1);
    const frac = baseFrac + (Math.random() - 0.5) * 0.2;
    const t = Math.max(0.05, Math.min(0.95, frac));

    // Interpolated position along straight line
    const midX = start.x + (end.x - start.x) * t;
    const midY = start.y + (end.y - start.y) * t;

    // Add lateral offset — wide but not extreme
    const lateralRange = Math.min(GAME_WIDTH, GAME_HEIGHT) * 0.35;
    const offsetX = (Math.random() - 0.5) * lateralRange;
    const offsetY = (Math.random() - 0.5) * lateralRange;

    // Clamp to map bounds
    const wpX = Math.max(margin, Math.min(GAME_WIDTH - margin, midX + offsetX));
    const wpY = Math.max(margin, Math.min(GAME_HEIGHT - margin, midY + offsetY));
    path.push({ x: wpX, y: wpY });
  }

  // Final destination: the base
  path.push({ x: end.x, y: end.y });
  return path;
}

/** After losing aggro target, recalculate A* path from current position. */
export function resyncToNearestWaypoint(enemy: Enemy): void {
  recalcPath(enemy);
}

/** Recalculate A* path from current position to base. */
export function recalcPath(enemy: Enemy): void {
  if (enemy.isFlyer) return;
  const newPath = findPathToBase(enemy.body.x, enemy.body.y);
  if (newPath && newPath.length > 1) {
    enemy.waypoints = newPath;
    enemy.currentWaypointIndex = 1; // skip first waypoint (current tile)
  }
  enemy.pathDirty = false;
  enemy.pathRecalcCooldown = GOD.pathRecalcInterval;
}

/** Clamp position to map bounds; ground enemies cannot enter walls. */
export function clampToValidPosition(enemy: Enemy, prevX: number, prevY: number): void {
  const margin = TILE_SIZE / 2;
  const x = Math.max(margin, Math.min(GAME_WIDTH - margin, enemy.body.x));
  const y = Math.max(margin, Math.min(GAME_HEIGHT - margin, enemy.body.y));
  if (enemy.isFlyer) {
    enemy.body.setPosition(x, y);
    return;
  }
  const col = Math.floor(x / TILE_SIZE);
  const row = Math.floor(y / TILE_SIZE);
  const tile = MAP_GRID[row]?.[col];
  const walkable = tile === TileType.PATH || tile === TileType.PLACEMENT ||
    tile === TileType.BROKEN_PLACEMENT || tile === TileType.BASE ||
    tile === TileType.SPAWN || tile === TileType.MUD || tile === TileType.GRILLE;
  if (walkable) {
    enemy.body.setPosition(x, y);
  } else {
    // Revert to previous valid position
    enemy.body.setPosition(prevX, prevY);
    enemy.markPathDirty();
  }
}

/** Base siege movement helper — only used internally by updateGround/updateFlyer. */
function updateBaseSiegeMovement(enemy: Enemy, dt: number): void {
  // Delegates to the enemy's own updateBaseSiege which handles melee base damage
  enemy.updateBaseSiege(dt);
}
